import { useDefaultValue } from '@gef-ui/context-defaults';
import { baseValidationTypes } from '@gef-ui/form-core/validations/constants';
import FieldTooltip from '@gef-ui/material/components/FieldTooltip';
import ValidationTooltip from '@gef-ui/material/components/ValidationTooltip';
import { passSx } from '@gef-ui/material/components/utils';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { useRadioGroup } from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import compose from "ramda/es/compose";
import curry from "ramda/es/curry";
import filter from "ramda/es/filter";
import map from "ramda/es/map";
import omit from "ramda/es/omit";
import pick from "ramda/es/pick";
import React, { forwardRef, Fragment, useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import { ALL_FIELDS, HIDDEN_HELPER_TEXT, validationDisplayTypes } from '../constants';
export const useFieldDefaultValue = ({ inProps, name })=>{
    const props = useDefaultValue({
        inProps,
        slice: 'FormMaterial',
        sliceDeprecated: [
            'FromMaterial'
        ],
        components: [
            ALL_FIELDS,
            name
        ]
    });
    return props;
};
export const resolveValidationColor = (validationType, validationDisplay)=>{
    if (validationDisplay === validationDisplayTypes.NONE) {
        return undefined;
    } else if (baseValidationTypes.REQUIRED === validationType || baseValidationTypes.ERROR === validationType) {
        return 'error';
    } else if (baseValidationTypes.WARNING === validationType) {
        return 'warning';
    } else if (baseValidationTypes.UNDERWRITING === validationType) {
        return 'underwriting';
    } else {
        return undefined;
    }
};
export const resolveHiddenProp = (hidden, { sx } = {})=>({
        sx: [
            hidden ? {
                display: 'none'
            } : undefined,
            ...passSx(sx)
        ]
    });
export const resolveVariant = (variant)=>{
    if (variant === 'static') {
        return {
            variant: 'filled',
            'data-gef-variant': 'static'
        };
    }
    return {
        variant
    };
};
export const resolveFormLabelVariant = (variant)=>{
    if (variant === 'static') {
        return {
            'data-gef-variant': 'static'
        };
    }
    return {};
};
export const resolveInputLabelProps = (variant, validationColor, { InputLabelProps = {} })=>{
    const base = {
        validation: validationColor,
        ...InputLabelProps
    };
    if (variant === 'static') {
        return {
            'data-gef-variant': 'static',
            ...base
        };
    }
    return base;
};
const radioProps = [
    'checked',
    'checkedIcon',
    'classes',
    'color',
    'disableRipple',
    'icon',
    'id',
    'inputProps',
    'inputRef',
    'name',
    'onChange',
    'required',
    'size',
    'sx'
];
export const pickRadioProps = pick(radioProps);
const RadioChildWrapper = ({ children, code })=>{
    const { value } = useRadioGroup();
    return value === String(code) ? children : null;
};
RadioChildWrapper.propTypes = {
    children: PropTypes.node,
    code: PropTypes.any
};
export const defaultRadioCreateOptions = curry(({ disabled, optionsFilter, valuePropName, labelPropName, validationColor, size }, options)=>compose(map(({ [valuePropName]: code, [labelPropName]: name, disabled: optionDisabled, children, ...other })=>{
        const props = pickRadioProps(other);
        const control = /*#__PURE__*/ React.createElement(FormControlLabel, {
            key: code,
            disabled: disabled || optionDisabled,
            sx: {
                marginLeft: 0
            },
            control: /*#__PURE__*/ React.createElement(Radio, {
                color: validationColor,
                disabled: disabled || optionDisabled,
                key: code,
                value: code,
                size: size,
                ...props
            }),
            label: name
        });
        if (children) {
            return /*#__PURE__*/ React.createElement(Fragment, {
                key: code
            }, control, /*#__PURE__*/ React.createElement(RadioChildWrapper, {
                code: code
            }, children));
        }
        return control;
    }), filter(optionsFilter))(options));
export const codelistProps = [
    'cacheKey',
    'name',
    'endpoint',
    'format',
    'keepPreviousData',
    'filterPathParam',
    'searchPathParam',
    'searchValue',
    'filterValue',
    'waitForFilterValue',
    'urlParams',
    'routeParams',
    'filterValueLocation',
    'baseUrlSelector'
];
export const omitCodelistProps = omit(codelistProps);
export const pickCodelistProps = pick(codelistProps);
export const codelistPropTypes = {
    name: PropTypes.string,
    endpoint: PropTypes.string,
    format: PropTypes.func,
    keepPreviousData: PropTypes.bool,
    filterPathParam: PropTypes.string,
    searchPathParam: PropTypes.string,
    searchValue: PropTypes.string,
    filterValue: PropTypes.string,
    waitForFilterValue: PropTypes.bool
};
export const createBaseInputProps = ({ tooltipValidation, validationColor, errorMessage, asyncValidationInProgress, tooltip, InputProps = {}, variant })=>{
    return {
        ...InputProps,
        validation: validationColor,
        endAdornment: /*#__PURE__*/ React.createElement(React.Fragment, null, InputProps?.endAdornment, asyncValidationInProgress && /*#__PURE__*/ React.createElement(CircularProgress, {
            size: 20
        }), tooltipValidation && /*#__PURE__*/ React.createElement(ValidationTooltip, {
            errorMessage: errorMessage,
            validationColor: validationColor
        }), tooltip && /*#__PURE__*/ React.createElement(FieldTooltip, {
            message: tooltip
        })),
        ...variant === 'static' ? {
            'data-gef-variant': 'static'
        } : {}
    };
};
export const useHelperText = (validationDisplay, text, helperText)=>{
    const shouldBeHidden = useMemo(()=>HIDDEN_HELPER_TEXT.includes(validationDisplay), [
        validationDisplay
    ]);
    return shouldBeHidden || !helperText && !text ? helperText : /*#__PURE__*/ React.createElement(React.Fragment, null, text && /*#__PURE__*/ React.createElement(Typography, {
        variant: "inherit",
        component: "span",
        color: "inherit",
        sx: {
            display: 'block'
        }
    }, text), helperText && /*#__PURE__*/ React.createElement(Typography, {
        variant: "inherit",
        component: "span",
        sx: {
            color: 'text.secondary',
            display: 'block'
        }
    }, helperText));
};
export const createPickerButtonSlot = (endAdornment)=>{
    const PickerButtonSlot = /*#__PURE__*/ forwardRef(({ children, ...other }, ref)=>/*#__PURE__*/ React.createElement(React.Fragment, null, endAdornment, /*#__PURE__*/ React.createElement(IconButton, {
            ...other,
            ref: ref
        }, children)));
    PickerButtonSlot.displayName = 'PickerButtonSlot';
    return PickerButtonSlot;
};
